import { useState, useEffect, useMemo } from 'react';
import { Timer, TrendingDown, Info, AlertCircle } from 'lucide-react';

interface HoldLimitDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (minutes: number) => void;
  currentMinutes: number;
  isLoading?: boolean;
}

// Preset intervals in minutes
const PRESET_INTERVALS = [0, 15, 20, 25, 30, 45, 60, 90, 120, 180, 240, 360, 480, 720, 1440];
const CUSTOM_VALUE = -1; // Special value to indicate custom mode
const MIN_CUSTOM_VALUE = 1;
const MAX_CUSTOM_VALUE = 1440;

/**
 * Format minutes into human-readable label
 */
function formatLabel(minutes: number): string {
  if (minutes === 0) return 'Never';
  if (minutes < 60) return `${minutes} min`;
  const hours = minutes / 60;
  if (hours % 1 === 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
  return `${hours.toFixed(1)} hours`;
}

/**
 * Generate dropdown options for hold limit
 */
function generateOptions(): { value: number; label: string }[] {
  const options: { value: number; label: string }[] = [];

  for (const minutes of PRESET_INTERVALS) {
    options.push({
      value: minutes,
      label: minutes === 0 ? 'Never (Disabled)' : formatLabel(minutes),
    });
  }

  // Add custom option at the end
  options.push({
    value: CUSTOM_VALUE,
    label: 'Custom...',
  });

  return options;
}

/**
 * Check if a value is a preset interval
 */
function isPresetValue(minutes: number): boolean {
  return PRESET_INTERVALS.includes(minutes);
}

/**
 * Validate custom input value
 */
function validateCustomValue(value: string): { isValid: boolean; error: string | null; parsedValue: number | null } {
  if (value.trim() === '') {
    return { isValid: false, error: 'Please enter a value', parsedValue: null };
  }

  const parsed = Number(value);

  if (isNaN(parsed)) {
    return { isValid: false, error: 'Please enter a valid number', parsedValue: null };
  }

  if (!Number.isInteger(parsed)) {
    return { isValid: false, error: 'Please enter a whole number', parsedValue: null };
  }

  if (parsed < MIN_CUSTOM_VALUE) {
    return { isValid: false, error: `Minimum is ${MIN_CUSTOM_VALUE} minute`, parsedValue: null };
  }

  if (parsed > MAX_CUSTOM_VALUE) {
    return { isValid: false, error: `Maximum is ${MAX_CUSTOM_VALUE} minutes (24 hours)`, parsedValue: null };
  }

  return { isValid: true, error: null, parsedValue: parsed };
}

export function HoldLimitDialog({
  isOpen,
  onClose,
  onConfirm,
  currentMinutes,
  isLoading = false,
}: HoldLimitDialogProps) {
  const [selectedMinutes, setSelectedMinutes] = useState(currentMinutes);
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [customInputValue, setCustomInputValue] = useState('');
  const options = generateOptions();

  // Determine the effective minutes value for display
  const effectiveMinutes = useMemo(() => {
    if (isCustomMode) {
      const validation = validateCustomValue(customInputValue);
      return validation.parsedValue ?? 0;
    }
    return selectedMinutes;
  }, [isCustomMode, customInputValue, selectedMinutes]);

  // Validate custom input
  const customValidation = useMemo(() => {
    if (!isCustomMode) return { isValid: true, error: null, parsedValue: null };
    return validateCustomValue(customInputValue);
  }, [isCustomMode, customInputValue]);

  // Determine if confirm button should be disabled
  const isConfirmDisabled = isLoading || (isCustomMode && !customValidation.isValid);

  // Update state when dialog opens with new current value
  useEffect(() => {
    if (isOpen) {
      // Check if currentMinutes is a preset value or custom
      if (isPresetValue(currentMinutes)) {
        setSelectedMinutes(currentMinutes);
        setIsCustomMode(false);
        setCustomInputValue('');
      } else {
        // Non-preset value: auto-select custom mode
        setSelectedMinutes(CUSTOM_VALUE);
        setIsCustomMode(true);
        setCustomInputValue(currentMinutes.toString());
      }
    }
  }, [isOpen, currentMinutes]);

  if (!isOpen) return null;

  const handleDropdownChange = (value: number) => {
    if (value === CUSTOM_VALUE) {
      setIsCustomMode(true);
      setSelectedMinutes(CUSTOM_VALUE);
      setCustomInputValue('');
    } else {
      setIsCustomMode(false);
      setSelectedMinutes(value);
      setCustomInputValue('');
    }
  };

  const handleConfirm = () => {
    if (isCustomMode) {
      const validation = validateCustomValue(customInputValue);
      if (validation.isValid && validation.parsedValue !== null) {
        onConfirm(validation.parsedValue);
      }
    } else {
      onConfirm(selectedMinutes);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
      data-testid="hold-limit-dialog-backdrop"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 space-y-6">
        {/* Title */}
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Stock Hold Limit
          </h2>
        </div>

        {/* Info Section */}
        <div className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-lg p-4 space-y-3 border border-orange-100 dark:border-orange-800">
          {/* Timer Info */}
          <div className="flex items-start space-x-3">
            <Timer className="w-5 h-5 text-orange-600 dark:text-orange-400 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-gray-600 dark:text-gray-300">Auto-Sell After</div>
              <div className="text-lg font-bold text-gray-900 dark:text-white" data-testid="auto-sell-display">
                {effectiveMinutes === 0 ? 'Disabled' : formatLabel(effectiveMinutes)}
              </div>
            </div>
          </div>

          {/* Auto-sell explanation - Only show if enabled */}
          {effectiveMinutes > 0 && (
            <div className="flex items-start space-x-3 pt-3 border-t border-orange-200 dark:border-orange-700">
              <TrendingDown className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-600 dark:text-gray-300">Hold Limit Behavior</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Positions held longer than <span className="font-semibold text-gray-900 dark:text-white">{formatLabel(effectiveMinutes)}</span> will be automatically sold at market price.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Dropdown */}
        <div>
          <label htmlFor="holdLimit" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Maximum hold time before auto-sell
          </label>
          <select
            id="holdLimit"
            data-testid="hold-limit-dropdown"
            value={isCustomMode ? CUSTOM_VALUE : selectedMinutes}
            onChange={(e) => handleDropdownChange(Number(e.target.value))}
            className="w-full pl-4 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base text-gray-900 dark:text-white bg-white dark:bg-gray-700 appearance-none"
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Custom Input - Only show when custom mode is active */}
        {isCustomMode && (
          <div>
            <label htmlFor="customMinutes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enter custom time (minutes)
            </label>
            <div className="relative">
              <input
                id="customMinutes"
                data-testid="custom-minutes-input"
                type="number"
                min={MIN_CUSTOM_VALUE}
                max={MAX_CUSTOM_VALUE}
                value={customInputValue}
                onChange={(e) => setCustomInputValue(e.target.value)}
                placeholder={`${MIN_CUSTOM_VALUE}-${MAX_CUSTOM_VALUE}`}
                className={`w-full pl-4 pr-16 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base text-gray-900 dark:text-white bg-white dark:bg-gray-700 ${
                  customValidation.error
                    ? 'border-red-500 dark:border-red-500'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 dark:text-gray-400">
                min
              </span>
            </div>
            {customValidation.error && (
              <div className="flex items-center mt-2 text-sm text-red-600 dark:text-red-400" data-testid="validation-error">
                <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0" />
                {customValidation.error}
              </div>
            )}
          </div>
        )}

        {/* Warning Text */}
        <div className="flex items-start space-x-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
          <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-gray-600 dark:text-gray-400">
            This applies to all webhook-triggered positions. Auto-sells are tagged as "HoldLimit-AutoSell" in your trade history. The check runs every minute.
          </p>
        </div>

        {/* Buttons */}
        <div className="flex space-x-3 pt-2">
          <button
            onClick={onClose}
            disabled={isLoading}
            data-testid="cancel-button"
            className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
            data-testid="confirm-button"
            className="flex-1 px-4 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Updating...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
